/** Padding of a message's plaintext to a size class (docs/FORMAT.md §3): `u32(len) ‖ data ‖ zeros`. Pure, no cryptography. */
import { PAD_BUCKETS, PAD_LENGTH_BYTES } from './labels';
import { DecryptError } from './primitives';

/** Padmé (Nikitin et al., 2019): rounds `n` up so that at most about log log n bits of its length remain visible. */
function padme(n: number): number {
  const exponent = Math.floor(Math.log2(n));
  const mantissaBits = Math.floor(Math.log2(exponent)) + 1;
  const zeroBits = exponent - mantissaBits;
  const mask = 2 ** zeroBits - 1;
  return Math.ceil(n / (mask + 1)) * (mask + 1);
}

/** The padded length of data that is `length` bytes long, 4-byte length field included. */
export function paddedLength(length: number): number {
  const needed = PAD_LENGTH_BYTES + length;
  for (const bucket of PAD_BUCKETS) if (needed <= bucket) return bucket;
  return padme(needed);
}

export function padPlaintext(data: Uint8Array): Uint8Array<ArrayBuffer> {
  if (data.length > 0xffffffff - PAD_LENGTH_BYTES) throw new RangeError('Too long to pad.');
  const out = new Uint8Array(new ArrayBuffer(paddedLength(data.length)));
  new DataView(out.buffer).setUint32(0, data.length);
  out.set(data, PAD_LENGTH_BYTES);
  return out;
}

/** The data inside a padded plaintext, or a DecryptError if its length field, its total length or its zero padding is wrong. */
export function unpadPlaintext(padded: Uint8Array): Uint8Array<ArrayBuffer> {
  if (padded.length < PAD_LENGTH_BYTES) throw new DecryptError('Invalid padding.');
  const length = new DataView(padded.buffer, padded.byteOffset, padded.byteLength).getUint32(0);
  if (length > padded.length - PAD_LENGTH_BYTES || paddedLength(length) !== padded.length) throw new DecryptError('Invalid padding.');
  const end = PAD_LENGTH_BYTES + length;
  for (let at = end; at < padded.length; at += 1) if (padded[at] !== 0) throw new DecryptError('Invalid padding.');
  return padded.slice(PAD_LENGTH_BYTES, end);
}
